/**
 * Window State Service
 *
 * Persists open window layouts (position, size, minimized, snap, params)
 * across page reloads. Uses localStorage as a fast local buffer so windows
 * are restored instantly before the backend settings round-trip completes.
 *
 * Storage strategy:
 *  1. Reads: localStorage first (instant), then Backend settings (cloud)
 *  2. Writes: localStorage immediately; backend via SettingsService (debounced
 *     by the caller in WindowsProvider, and only after the backend state has
 *     been read, #908)
 *
 * What is saved per window:
 *  - appId, position, size, minimized, isMaximized, snapSide, params, subtitle
 *
 * What is NOT saved (re-derived on restore):
 *  - id (new UUID generated on restore)
 *  - zIndex (normalized to 1000 + index on restore)
 *  - createdAt (set to Date.now() on restore)
 *  - isCustomPosition (always true after restore — user had it placed)
 *  - title (re-translated from appId on restore)
 */

import type { PersistedWindowState } from '@/types/settings'
import type { WindowItem } from '@/hooks/useWindows'
import type { WindowParams } from '@/types/window'
import { logError, logInfo } from '@/lib/logger'
import { authService } from './auth-service'
import { APP_ID_ALIASES, RETIRED_APP_IDS } from '@/data/apps'

const LS_KEY_PREFIX = 'cdb-window-state'

function getLocalStorageKey(): string {
  const user = authService.getCachedUser()
  return user ? `${LS_KEY_PREFIX}-${user.id}` : LS_KEY_PREFIX
}

/** Validate that a persisted position is within a reasonable screen area */
function isPositionValid(pos: PersistedWindowState['position']): boolean {
  if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return false
  if (typeof window === 'undefined') return true
  // Allow some overhang — user must be able to reach the titlebar (y ≥ 0)
  return pos.x > -200 && pos.y >= 0 && pos.x < window.innerWidth + 200
}

/** Validate that a persisted size is within usable bounds */
function isSizeValid(size: PersistedWindowState['size']): boolean {
  if (!size || typeof size.w !== 'number' || typeof size.h !== 'number') return false
  const minW = 200
  const minH = 100
  const maxW = typeof window !== 'undefined' ? window.innerWidth * 2 : 4000
  const maxH = typeof window !== 'undefined' ? window.innerHeight * 2 : 3000
  return size.w >= minW && size.h >= minH && size.w <= maxW && size.h <= maxH
}

/** Extract the persistable subset from a live WindowItem */
export function serializeWindow(w: WindowItem): PersistedWindowState {
  return {
    appId: w.appId,
    subtitle: w.subtitle,
    minimized: w.minimized,
    isMaximized: w.isMaximized,
    position: { x: w.position.x, y: w.position.y },
    size: { w: w.size.w, h: w.size.h },
    snapSide: w.snapSide,
    params: w.params ? persistableParams(w.params) : undefined,
  }
}

/** A blob URL is gone after a reload; a window that shows a file keeps its
 *  `FileRef` instead (#1619). */
function persistableParams(params: WindowParams): Record<string, string | number | boolean> {
  const kept: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (typeof value === 'string' && value.startsWith('blob:')) continue
    kept[key] = value
  }
  return kept
}


/** Validate and sanitize a single persisted window entry */
function sanitize(w: PersistedWindowState): PersistedWindowState {
  const position = isPositionValid(w.position)
    ? w.position
    : { x: 80, y: 80 }

  const size = isSizeValid(w.size)
    ? w.size
    : { w: 900, h: 600 }

  // Saved under an old id (`admin`, `pdf-viewer`, …): restore as the app it became.
  const appId = APP_ID_ALIASES[w.appId] ?? w.appId

  return { ...w, appId, position, size, isMaximized: Boolean(w.isMaximized) }
}

// ─── localStorage helpers ────────────────────────────────────────────────────

export function saveToLocalStorage(windows: PersistedWindowState[]): void {
  try {
    localStorage.setItem(getLocalStorageKey(), JSON.stringify(windows))
  } catch (e) {
    logError(e as Error, { context: 'WindowStateService.saveToLocalStorage' })
  }
}

export function loadFromLocalStorage(): PersistedWindowState[] {
  try {
    const raw = localStorage.getItem(getLocalStorageKey())
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return (parsed as PersistedWindowState[])
      .filter((w) => w && typeof w.appId === 'string')
      .map(sanitize)
      .filter((w) => !RETIRED_APP_IDS.has(w.appId))
  } catch {
    return []
  }
}

const SKIP_RESTORE_KEY = 'cdb-window-state-skip-restore'

export function clearLocalStorage(): void {
  try {
    localStorage.removeItem(getLocalStorageKey())
    // Set flag so the next mount skips both local and backend restoration
    localStorage.setItem(SKIP_RESTORE_KEY, '1')
  } catch {
    // ignore
  }
}

/**
 * Check and consume the skip-restore flag.
 * Returns true (and clears the flag) if window restoration should be skipped.
 */
export function consumeSkipRestore(): boolean {
  try {
    if (localStorage.getItem(SKIP_RESTORE_KEY)) {
      localStorage.removeItem(SKIP_RESTORE_KEY)
      return true
    }
  } catch {
    // ignore
  }
  return false
}

// ─── Backend helpers (via SettingsService) ───────────────────────────────────

/**
 * Schreibt nur `windowConfigurations`. Läuft bei jeder Fensteränderung —
 * als Full-Save über geladene Settings war das der häufigste Weg, auf dem
 * Theme, Apps und Ordner mit Standardwerten überschrieben wurden (#908).
 */
export async function saveToBackend(windows: PersistedWindowState[]): Promise<void> {
  const { SettingsService } = await import('./settings-service')
  const success = await SettingsService.patchSettings({ windowConfigurations: windows })
  if (success) {
    logInfo({ context: 'WindowStateService' }, `Saved ${windows.length} windows to backend`)
  }
}

/**
 * Wirft bei Backend-Fehlern. Ein leeres Array heißt „keine Fenster
 * gespeichert" — ein Fehler darf nicht genauso aussehen, sonst gewinnt der
 * lokale Stand und wird zurückgeschrieben.
 */
export async function loadFromBackend(): Promise<PersistedWindowState[]> {
  const { SettingsService } = await import('./settings-service')
  const settings = await SettingsService.loadUserSettings()
  const list = settings.windowConfigurations
  if (!Array.isArray(list)) return []
  return list
    .filter((w) => w && typeof w.appId === 'string')
    .map(sanitize)
    .filter((w) => !RETIRED_APP_IDS.has(w.appId))
}
